import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';

import { formatPrice } from '@spotahome/soyuz-currency';

import AdUrlService from '../../utils/ad-url-service';
import { useWoosmapScript } from '../../utils/woosmap-script';
import { useHighlightMarkersContext } from '../../context/HighlightMarkersContext';

import { POI_CIRCLE_COLOR, createPoiMarkerOverlay } from './poi-overlay';
import { createPriceMarkerOverlay } from './price-marker-overlay';

import './MapWoosmap.scss';

const NO_OP = () => {};
const DEFAULT_CENTER_COORDINATES = { lat: 0, lng: 0 };
const BIG_RESOLUTION_BREAKPOINT = 1920;
const MARKER_SHIFT_BIG = 0.35;
const MARKER_SHIFT_SMALL = 0.4;

const POI_CIRCLE_RADIUS_METERS = 1000;

const buildLatLng = (lat, lng) => ({
  lat: parseFloat(lat),
  lng: parseFloat(lng)
});

const readLatLng = pt => ({
  lat: typeof pt.lat === 'function' ? pt.lat() : pt.lat,
  lng: typeof pt.lng === 'function' ? pt.lng() : pt.lng
});

const MapWoosmap = ({
  pointOfInterest = null,
  showPrice = true,
  onZoomChange = NO_OP,
  defaultZoom = 11,
  onMapDrag = NO_OP,
  centerCoordinates = DEFAULT_CENTER_COORDINATES,
  mapId,
  markerList,
  currencyIsoCode,
  onIdle = null,
  onMarkerClicked,
  onMapDidMount,
  onMapWillUnmount,
  shouldUpdateMapCenter = true
}) => {
  const map = useRef(null);
  const markers = useRef({});
  const poiMarker = useRef(null);
  const poiCircle = useRef(null);
  const listeners = useRef([]);
  const prevMarkers = useRef(markerList);

  const { handleMarkerClicked, homecardMouseEnterId } =
    useHighlightMarkersContext();
  const activeMarkerId = useRef(null);

  const panToMarker = position => {
    if (!map.current) return;
    const bounds = map.current.getBounds();
    if (!bounds) return;
    const ne = readLatLng(bounds.getNorthEast());
    const sw = readLatLng(bounds.getSouthWest());
    const shift =
      window.screen.availWidth > BIG_RESOLUTION_BREAKPOINT
        ? MARKER_SHIFT_BIG
        : MARKER_SHIFT_SMALL;
    const latShift = (ne.lat - sw.lat) * shift;
    map.current.panTo({
      lat: position.lat + latShift,
      lng: position.lng
    });
  };

  const handleClick = (markerId, position) => {
    handleMarkerClicked(markerId);
    onMarkerClicked(markerId);
    panToMarker(position);
  };

  const getInitialCenter = () => {
    const mapCenter =
      typeof window !== 'undefined' &&
      AdUrlService.getQueryParamValue('mapCenter');

    if (mapCenter) {
      const [lat, lng] = mapCenter.split(',');
      return buildLatLng(lat, lng);
    }

    return buildLatLng(centerCoordinates.lat, centerCoordinates.lng);
  };

  const buildMarkerLabel = marker => {
    if (!showPrice || marker.silent || marker.so) return null;
    return formatPrice(marker.minimumPrice, currencyIsoCode);
  };

  const addMarker = marker => {
    const [lng, lat] = marker.coord;
    const label = buildMarkerLabel(marker);
    const position = { lat, lng };

    if (label) {
      const overlay = createPriceMarkerOverlay(position, label, () => {
        handleClick(marker.id, position);
      });
      overlay.setMap(map.current);
      markers.current[marker.id] = overlay;
      return;
    }

    const wm = new window.woosmap.map.Marker({
      position,
      map: map.current
    });
    const clickListener = wm.addListener('click', () => {
      handleClick(marker.id, position);
    });
    listeners.current.push(clickListener);
    markers.current[marker.id] = wm;
  };

  const removeMarker = markerId => {
    const wm = markers.current[markerId];
    if (!wm) return;
    wm.setMap(null);
    delete markers.current[markerId];
  };

  const renderPointOfInterest = poi => {
    if (poiMarker.current) {
      poiMarker.current.setMap(null);
      poiMarker.current = null;
    }
    if (poiCircle.current) {
      poiCircle.current.setMap(null);
      poiCircle.current = null;
    }

    if (!poi) return;

    const position = { lat: poi.lat, lng: poi.lng };

    poiMarker.current = createPoiMarkerOverlay(position);
    poiMarker.current.setMap(map.current);

    poiCircle.current = new window.woosmap.map.Circle({
      map: map.current,
      center: position,
      radius: POI_CIRCLE_RADIUS_METERS,
      strokeColor: POI_CIRCLE_COLOR,
      strokeOpacity: 1,
      strokeWeight: 1,
      fillColor: POI_CIRCLE_COLOR,
      fillOpacity: 0.25
    });
  };

  const getBoundsShape = () => {
    const bounds = map.current?.getBounds();
    if (!bounds) return null;
    return {
      northEast: readLatLng(bounds.getNorthEast()),
      southWest: readLatLng(bounds.getSouthWest())
    };
  };

  const getIdleBoundsShape = () => {
    const bounds = map.current?.getBounds();
    if (!bounds) return null;
    const ne = readLatLng(bounds.getNorthEast());
    const sw = readLatLng(bounds.getSouthWest());
    const container = document.getElementById(mapId);
    return {
      n: ne.lat,
      s: sw.lat,
      e: ne.lng,
      w: sw.lng,
      zoom: Math.round(map.current.getZoom()),
      width: container?.offsetWidth ?? 0,
      height: container?.offsetHeight ?? 0
    };
  };

  const buildMapAdapter = () => ({
    setZoom: zoom => map.current.setZoom(zoom),
    getZoom: () => map.current.getZoom(),
    setCenter: coords =>
      map.current.setCenter(buildLatLng(coords.lat, coords.lng)),
    getMapBounds: getIdleBoundsShape
  });

  const handleDragEnd = () => {
    const center = readLatLng(map.current.getCenter());
    const mapCenterStr = `${center.lat},${center.lng}`;
    if (shouldUpdateMapCenter) {
      AdUrlService.setQueryParameter('mapCenter', mapCenterStr);
    }
    const bounds = getBoundsShape();
    if (bounds) onZoomChange(bounds);
    onMapDrag(mapCenterStr);
  };

  const handleZoomChanged = () => {
    const bounds = getBoundsShape();
    if (bounds) onZoomChange(bounds);
  };

  const handleIdle = () => {
    if (!onIdle) return;
    onIdle(getIdleBoundsShape());
  };

  const handleMapLoad = () => {
    const container = document.getElementById(mapId);
    if (!container) return;

    map.current = new window.woosmap.map.Map(container, {
      center: getInitialCenter(),
      zoom: defaultZoom,
      disableTilt: true
    });

    listeners.current.push(
      map.current.addListener('dragend', handleDragEnd),
      map.current.addListener('zoom_changed', handleZoomChanged)
    );

    // Emit one synthetic init onIdle once the map's first natural idle
    // settles, then start forwarding subsequent idle events. Search.js
    // consumes the first onIdle as a userHasInteractedWithTheMap=false
    // signal — without this, the user's first zoom gets misclassified
    // as init and getMarkers doesn't run.
    const initListener = map.current.addListener('idle', () => {
      initListener.remove();
      handleIdle();
      listeners.current.push(map.current.addListener('idle', handleIdle));
    });
    listeners.current.push(initListener);

    markerList.forEach(addMarker);
    renderPointOfInterest(pointOfInterest);

    if (onMapDidMount) {
      onMapDidMount(buildMapAdapter());
    }
  };

  useWoosmapScript(handleMapLoad);

  useEffect(() => {
    return () => {
      listeners.current.forEach(l => l.remove());
      listeners.current = [];
      Object.keys(markers.current).forEach(removeMarker);
      if (poiMarker.current) {
        poiMarker.current.setMap(null);
        poiMarker.current = null;
      }
      if (poiCircle.current) {
        poiCircle.current.setMap(null);
        poiCircle.current = null;
      }
      onMapWillUnmount();
    };
  }, []);

  useEffect(() => {
    if (!map.current) return;
    if (isEqual(prevMarkers.current, markerList)) return;

    const prevIds = new Set(prevMarkers.current.map(m => m.id));
    const nextIds = new Set(markerList.map(m => m.id));

    prevMarkers.current.forEach(m => {
      if (!nextIds.has(m.id)) removeMarker(m.id);
    });
    markerList.forEach(m => {
      if (!prevIds.has(m.id)) addMarker(m);
    });

    prevMarkers.current = markerList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerList]);

  useEffect(() => {
    if (!map.current) return;
    renderPointOfInterest(pointOfInterest);
  }, [pointOfInterest?.lat, pointOfInterest?.lng]);

  useEffect(() => {
    if (activeMarkerId.current) {
      const prev = markers.current[activeMarkerId.current];
      prev?.setActive?.(false);
    }
    if (homecardMouseEnterId) {
      const next = markers.current[homecardMouseEnterId];
      next?.setActive?.(true);
      activeMarkerId.current = homecardMouseEnterId;
    } else {
      activeMarkerId.current = null;
    }
  }, [homecardMouseEnterId]);

  return <div className="l-map l-map--woosmap" id={mapId} data-cy="themap" />;
};

MapWoosmap.propTypes = {
  mapId: PropTypes.string.isRequired,
  centerCoordinates: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
  }),
  defaultZoom: PropTypes.number,
  markerList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  pointOfInterest: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    name: PropTypes.string,
    placeId: PropTypes.string,
    cityId: PropTypes.string
  }),
  onMarkerClicked: PropTypes.func.isRequired,
  onMapDidMount: PropTypes.func.isRequired,
  onMapWillUnmount: PropTypes.func.isRequired,
  onIdle: PropTypes.func,
  onZoomChange: PropTypes.func,
  currencyIsoCode: PropTypes.string.isRequired,
  showPrice: PropTypes.bool,
  onMapDrag: PropTypes.func,
  shouldUpdateMapCenter: PropTypes.bool
};

export default MapWoosmap;
